<template>
  <div class="notification-logs">
    <!-- Stats Cards -->
    <div v-if="stats" class="stats-grid">
      <div class="stat-card">
        <div class="stat-icon success">
          <i class="fas fa-check-circle"></i>
        </div>
        <div class="stat-info">
          <span class="stat-value">{{ stats.total_enviados }}</span>
          <span class="stat-label">Enviados</span>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon info">
          <i class="fas fa-paper-plane"></i>
        </div>
        <div class="stat-info">
          <span class="stat-value">{{ stats.total_entregues }}</span>
          <span class="stat-label">Entregues</span>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon danger">
          <i class="fas fa-times-circle"></i>
        </div>
        <div class="stat-info">
          <span class="stat-value">{{ stats.total_falhas }}</span>
          <span class="stat-label">Falhas</span>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon warning">
          <i class="fas fa-clock"></i>
        </div>
        <div class="stat-info">
          <span class="stat-value">{{ stats.total_pendentes }}</span>
          <span class="stat-label">Pendentes</span>
        </div>
      </div>
    </div>

    <!-- Filtros -->
    <div class="filtros-section">
      <div class="filtros-header">
        <span v-if="pollingAtivo" class="auto-refresh-badge">
          <i class="fas fa-sync-alt fa-spin"></i> Atualizando a cada 10s
        </span>
      </div>
      <div class="filtros-row">
        <select v-model="filtros.canal" class="select-filtro" @change="carregarLogs">
          <option value="">Todos os canais</option>
          <option value="email">Email</option>
          <option value="whatsapp">WhatsApp</option>
        </select>
        <select v-model="filtros.status" class="select-filtro" @change="carregarLogs">
          <option value="">Todos os status</option>
          <option value="pendente">Pendente</option>
          <option value="enviado">Enviado</option>
          <option value="entregue">Entregue</option>
          <option value="falha">Falha</option>
        </select>
        <input
          v-model="filtros.data_inicio"
          type="date"
          class="input-data"
          @change="carregarLogs"
        />
        <input
          v-model="filtros.data_fim"
          type="date"
          class="input-data"
          @change="carregarLogs"
        />
        <button class="btn-limpar" @click="limparFiltros">
          <i class="fas fa-times"></i> Limpar
        </button>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="loading-container">
      <div class="spinner"></div>
      <span>Carregando histórico...</span>
    </div>

    <!-- Lista vazia -->
    <div v-else-if="logs.length === 0" class="empty-state">
      <i class="fas fa-inbox"></i>
      <p>Nenhum registro encontrado</p>
    </div>

    <!-- Tabela de logs -->
    <div v-else class="logs-table-wrapper">
      <table class="logs-table">
        <thead>
          <tr>
            <th>Destinatário</th>
            <th>Canal</th>
            <th>Campanha</th>
            <th>Status</th>
            <th>Data</th>
            <th>Erro</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="log in logs" :key="log.id">
            <td class="col-destinatario">
              <div class="destinatario-info">
                <strong v-if="log.paciente">
                  {{ log.paciente.primeiro_nome }} {{ log.paciente.sobrenome }}
                </strong>
                <span class="destinatario-contato">{{ log.destinatario }}</span>
              </div>
            </td>
            <td>
              <span class="canal-badge" :class="log.canal">
                <i :class="log.canal === 'email' ? 'fas fa-envelope' : 'fab fa-whatsapp'"></i>
              </span>
            </td>
            <td class="col-campanha">
              <span v-if="log.campaign">{{ log.campaign.nome }}</span>
              <span v-else class="text-muted">-</span>
            </td>
            <td>
              <span class="status-badge" :class="log.status">
                <i :class="getStatusIcon(log.status)"></i>
                {{ getStatusLabel(log.status) }}
              </span>
            </td>
            <td class="col-data">
              {{ formatarData(log.enviado_em || log.created_at) }}
            </td>
            <td class="col-erro">
              <span v-if="log.erro" class="erro-text" :title="log.erro">
                {{ truncateText(log.erro, 50) }}
              </span>
              <span v-else class="text-muted">-</span>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Paginação -->
      <div v-if="paginacao.last_page > 1" class="paginacao">
        <button
          class="btn-page"
          :disabled="paginacao.current_page === 1"
          @click="mudarPagina(1)"
        >
          <i class="fas fa-angle-double-left"></i>
        </button>
        <button
          class="btn-page"
          :disabled="paginacao.current_page === 1"
          @click="mudarPagina(paginacao.current_page - 1)"
        >
          <i class="fas fa-angle-left"></i>
        </button>
        <span class="page-info">
          Página {{ paginacao.current_page }} de {{ paginacao.last_page }}
          ({{ paginacao.total }} registros)
        </span>
        <button
          class="btn-page"
          :disabled="paginacao.current_page === paginacao.last_page"
          @click="mudarPagina(paginacao.current_page + 1)"
        >
          <i class="fas fa-angle-right"></i>
        </button>
        <button
          class="btn-page"
          :disabled="paginacao.current_page === paginacao.last_page"
          @click="mudarPagina(paginacao.last_page)"
        >
          <i class="fas fa-angle-double-right"></i>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue'
import dayjs from 'dayjs'

const props = defineProps({
  logs: { type: Array, default: () => [] },
  stats: { type: Object, default: null },
  paginacao: {
    type: Object,
    default: () => ({
      current_page: 1,
      last_page: 1,
      per_page: 50,
      total: 0,
    }),
  },
  loading: { type: Boolean, default: false },
  autoRefresh: { type: Boolean, default: true },
})

const emit = defineEmits(['carregar', 'carregar-stats'])

// Polling para atualização automática
let pollingInterval = null
const pollingAtivo = ref(false)

const filtros = ref({
  canal: '',
  status: '',
  data_inicio: '',
  data_fim: '',
})

function carregarLogs(pagina = 1) {
  emit('carregar', filtros.value, pagina)
}

function limparFiltros() {
  filtros.value = {
    canal: '',
    status: '',
    data_inicio: '',
    data_fim: '',
  }
  carregarLogs()
}

function mudarPagina(pagina) {
  carregarLogs(pagina)
}

function getStatusIcon(status) {
  const icons = {
    pendente: 'fas fa-clock',
    enviado: 'fas fa-paper-plane',
    entregue: 'fas fa-check-circle',
    falha: 'fas fa-times-circle',
  }
  return icons[status] || 'fas fa-question-circle'
}

function getStatusLabel(status) {
  const labels = {
    pendente: 'Pendente',
    enviado: 'Enviado',
    entregue: 'Entregue',
    falha: 'Falha',
  }
  return labels[status] || status
}

function formatarData(data) {
  if (!data) return '-'
  return dayjs(data).format('DD/MM/YY HH:mm')
}

function truncateText(text, maxLength) {
  if (!text) return ''
  if (text.length <= maxLength) return text
  return text.substring(0, maxLength) + '...'
}

function iniciarPolling() {
  if (pollingInterval || !props.autoRefresh) return
  pollingAtivo.value = true
  pollingInterval = setInterval(() => {
    emit('carregar-stats')
    emit('carregar', filtros.value, props.paginacao.current_page)
  }, 10000) // Atualiza a cada 10 segundos
}

function pararPolling() {
  if (pollingInterval) {
    clearInterval(pollingInterval)
    pollingInterval = null
    pollingAtivo.value = false
  }
}

onMounted(() => {
  emit('carregar-stats')
  carregarLogs()
  iniciarPolling()
})

onBeforeUnmount(() => {
  pararPolling()
})
</script>

<style scoped>
.notification-logs {
  padding: 20px;
}

/* Stats Grid */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 15px;
  margin-bottom: 25px;
}

.stat-card {
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 15px;
}

.stat-icon {
  width: 50px;
  height: 50px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
}

.stat-icon.success {
  background: #e8f5e9;
  color: #388e3c;
}

.stat-icon.info {
  background: #e3f2fd;
  color: #1976d2;
}

.stat-icon.danger {
  background: #ffebee;
  color: #c62828;
}

.stat-icon.warning {
  background: #fff3e0;
  color: #f57c00;
}

.stat-info {
  display: flex;
  flex-direction: column;
}

.stat-value {
  font-size: 24px;
  font-weight: 700;
  color: #333;
}

.stat-label {
  font-size: 13px;
  color: #666;
}

/* Filtros */
.filtros-section {
  margin-bottom: 20px;
}

.filtros-header {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 10px;
}

.auto-refresh-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  background: #e3f2fd;
  color: #1976d2;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
}

.dark-mode .auto-refresh-badge {
  background: #1e3a5f;
  color: #64b5f6;
}

.filtros-row {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.select-filtro,
.input-data {
  padding: 10px 15px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  background: white;
}

.btn-limpar {
  padding: 10px 15px;
  background: #f5f5f5;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  color: #666;
}

.btn-limpar:hover {
  background: #e0e0e0;
}

/* Loading & Empty */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px;
  color: #666;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #4caf50;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 15px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: #666;
}

.empty-state i {
  font-size: 48px;
  margin-bottom: 15px;
  color: #ccc;
}

/* Table */
.logs-table-wrapper {
  overflow-x: auto;
}

.logs-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.logs-table th,
.logs-table td {
  padding: 12px 15px;
  text-align: left;
  border-bottom: 1px solid #eee;
}

.logs-table th {
  background: #f8f9fa;
  font-weight: 600;
  color: #555;
  white-space: nowrap;
}

.logs-table tr:hover {
  background: #f8f9fa;
}

.col-destinatario {
  min-width: 200px;
}

.destinatario-info {
  display: flex;
  flex-direction: column;
}

.destinatario-info strong {
  color: #333;
}

.destinatario-contato {
  font-size: 12px;
  color: #888;
}

.col-campanha {
  max-width: 200px;
}

.col-data {
  white-space: nowrap;
  color: #666;
}

.col-erro {
  max-width: 200px;
}

.text-muted {
  color: #aaa;
}

.erro-text {
  color: #c62828;
  font-size: 12px;
  cursor: help;
}

.canal-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  font-size: 14px;
}

.canal-badge.email {
  background: #e3f2fd;
  color: #1976d2;
}

.canal-badge.whatsapp {
  background: #e8f5e9;
  color: #388e3c;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
}

.status-badge.pendente {
  background: #fff3e0;
  color: #f57c00;
}

.status-badge.enviado {
  background: #e3f2fd;
  color: #1976d2;
}

.status-badge.entregue {
  background: #e8f5e9;
  color: #388e3c;
}

.status-badge.falha {
  background: #ffebee;
  color: #c62828;
}

/* Paginação */
.paginacao {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #eee;
}

.btn-page {
  width: 36px;
  height: 36px;
  border: 1px solid #ddd;
  border-radius: 6px;
  background: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666;
}

.btn-page:hover:not(:disabled) {
  background: #f5f5f5;
  border-color: #ccc;
}

.btn-page:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-info {
  font-size: 14px;
  color: #666;
  padding: 0 10px;
}

/* Dark mode */
.dark-mode .stat-card {
  background: #2a2a2a;
  border-color: #444;
}

.dark-mode .stat-value {
  color: #f1f1f1;
}

.dark-mode .logs-table th {
  background: #2a2a2a;
  color: #ccc;
}

.dark-mode .logs-table td {
  border-color: #444;
}

.dark-mode .logs-table tr:hover {
  background: #2a2a2a;
}

.dark-mode .destinatario-info strong {
  color: #f1f1f1;
}

.dark-mode .select-filtro,
.dark-mode .input-data {
  background: #2a2a2a;
  border-color: #444;
  color: #f1f1f1;
}

@media (max-width: 768px) {
  .filtros-row {
    flex-direction: column;
  }

  .select-filtro,
  .input-data,
  .btn-limpar {
    width: 100%;
  }

  .logs-table {
    font-size: 12px;
  }

  .logs-table th,
  .logs-table td {
    padding: 8px 10px;
  }

  .paginacao {
    flex-wrap: wrap;
  }

  .page-info {
    width: 100%;
    text-align: center;
    order: -1;
    margin-bottom: 10px;
  }
}
</style>
